// src/SuperAdminDashboard.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth, callDeleteClient } from './firebase';
import { signOut } from 'firebase/auth';
import { collection, query, onSnapshot, doc, updateDoc, increment, setDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ServerManagement from './components/ServerManagement';
import ServerStats from './components/ServerStats';
import TransactionHistory from './components/TransactionHistory';

function SuperAdminDashboard({ user, superAdminData }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all'); // 🔥 جديد: فلتر المنصة
  const [selectedClient, setSelectedClient] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [orderCost, setOrderCost] = useState(''); // 🔥 جديد: لتعديل سعر الرسالة
  const [feedbackCost, setFeedbackCost] = useState(''); // 🔥 جديد: لتعديل سعر الـ Feedback
  const [bostaCost, setBostaCost] = useState(''); // 🔥 جديد: لتعديل سعر الـ Bosta
  const [modalType, setModalType] = useState(''); // 🔥 جديد: نوع المودال (balance/orderCost/feedbackCost/bostaCost)
  const [isProcessing, setIsProcessing] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(0); // 🔥 جديد: إجمالي الإيرادات
  const navigate = useNavigate();

  // جلب جميع العملاء
  useEffect(() => {
    setLoading(true);
    const clientsQuery = query(collection(db, 'clients_api'));
    
    const unsubscribe = onSnapshot(clientsQuery, (snapshot) => {
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📊 Clients updated:', clientsData.length, 'clients');
      console.log('💰 Total balance:', clientsData.reduce((sum, c) => sum + (c.balance || 0), 0));
      setClients(clientsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching clients:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 🔥 جلب إجمالي الإيرادات الكلية
  useEffect(() => {
    const trackingQuery = query(collection(db, 'financial_tracking'), doc(db, 'financial_tracking', 'total_revenue'));
    
    const unsubscribe = onSnapshot(doc(db, 'financial_tracking', 'total_revenue'), (snapshot) => {
      if (snapshot.exists()) {
        setTotalRevenue(snapshot.data().amount || 0);
      } else {
        setTotalRevenue(0);
      }
    }, (error) => {
      console.error("Error fetching total revenue:", error);
      setTotalRevenue(0);
    });

    return () => unsubscribe();
  }, []);

  // جلب جميع الأوردرات
  useEffect(() => {
    const ordersQuery = query(collection(db, 'final_orders_api'));
    
    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllOrders(ordersData);
    }, (error) => {
      console.error("Error fetching orders:", error);
    });

    return () => unsubscribe();
  }, []);

  // تسجيل الخروج
  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate('/');
    }).catch((error) => {
      console.error('Logout Error:', error);
    });
  };

  // فلترة العملاء بالبحث والمنصة
  const filteredClients = useMemo(() => {
    let filtered = clients;
    
    // 🔥 فلتر حسب المنصة
    if (platformFilter !== 'all') {
      filtered = filtered.filter(c => c.platform === platformFilter);
    }
    
    // فلتر حسب البحث
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(client => 
        client.brand_name?.toLowerCase().includes(search) ||
        client.email?.toLowerCase().includes(search) ||
        client.brand_owner_whatsapp?.toString().includes(search)
      );
    }
    
    return filtered;
  }, [clients, platformFilter, searchTerm]);

  // إحصائيات
  const stats = useMemo(() => {
    const totalClients = clients.length;
    const activeClients = clients.filter(c => c.isActive).length;
    const inactiveClients = totalClients - activeClients;
    const totalBalance = clients.reduce((sum, c) => sum + (c.balance || 0), 0);
    const botsActive = clients.filter(c => c.chatbot).length;
    
    // 🔥 إحصائيات المنصات
    const shopifyClients = clients.filter(c => c.platform === 'shopify').length;
    const easyorderClients = clients.filter(c => c.platform === 'easyorder').length;
    
    // إحصائيات الأوردرات الكلية
    const totalConfirmed = allOrders.filter(o => o.final_status === 'CONFIRM').length;
    const totalCanceled = allOrders.filter(o => o.final_status === 'CANCELED').length;
    const totalAiCreated = allOrders.filter(o => o.final_status === 'AI_CREATED').length;
    const totalUnclear = allOrders.filter(o => o.final_status === 'UNCLEAR').length;
    const totalOrders = allOrders.length;
    
    return { 
      totalClients, 
      activeClients, 
      inactiveClients, 
      totalBalance, 
      botsActive,
      shopifyClients,
      easyorderClients,
      totalConfirmed,
      totalCanceled,
      totalAiCreated,
      totalUnclear,
      totalOrders
    };
  }, [clients, allOrders]);

  // دالة للحصول على إحصائيات عميل محدد
  const getClientOrderStats = useCallback((brandName) => {
    const clientOrders = allOrders.filter(o => o.brand_name === brandName);
    return {
      total: clientOrders.length,
      confirmed: clientOrders.filter(o => o.final_status === 'CONFIRM').length,
      canceled: clientOrders.filter(o => o.final_status === 'CANCELED').length,
      aiCreated: clientOrders.filter(o => o.final_status === 'AI_CREATED').length,
      unclear: clientOrders.filter(o => o.final_status === 'UNCLEAR').length,
    };
  }, [allOrders]);

  // تبديل حالة الخدمة
  const toggleServiceStatus = async (clientId, currentStatus) => {
    if (isProcessing) return;
    
    const confirmMsg = currentStatus 
      ? "هل تريد إيقاف الخدمة لهذا العميل؟" 
      : "هل تريد تفعيل الخدمة لهذا العميل؟";
    
    if (!window.confirm(confirmMsg)) return;
    
    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', clientId);
      await updateDoc(clientRef, {
        isActive: !currentStatus
      });
    } catch (error) {
      console.error("Error updating service status:", error);
      alert("حدث خطأ أثناء تغيير حالة الخدمة");
    } finally {
      setIsProcessing(false);
    }
  };

  // تبديل حالة البوت
  const toggleBotStatus = async (clientId, currentStatus) => {
    if (isProcessing) return;
    
    const confirmMsg = currentStatus 
      ? "هل تريد إيقاف البوت لهذا العميل؟" 
      : "هل تريد تفعيل البوت لهذا العميل؟";
    
    if (!window.confirm(confirmMsg)) return;
    
    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', clientId);
      await updateDoc(clientRef, {
        chatbot: !currentStatus
      });
    } catch (error) {
      console.error("Error updating bot status:", error);
      alert("حدث خطأ أثناء تغيير حالة البوت");
    } finally {
      setIsProcessing(false);
    }
  };

  // فتح مودال تعديل الرصيد
  const openBalanceModal = (client) => {
    setSelectedClient(client);
    setBalanceAmount('');
    setModalType('balance');
  };

  // فتح مودال تعديل سعر الرسالة
  const openOrderCostModal = (client) => {
    setSelectedClient(client);
    setOrderCost(client.order_cost || 3);
    setModalType('orderCost');
  };

  // فتح مودال تعديل سعر Feedback
  const openFeedbackCostModal = (client) => {
    setSelectedClient(client);
    setFeedbackCost(client.feedback_cost || 0);
    setModalType('feedbackCost');
  };

  // فتح مودال تعديل سعر Bosta
  const openBostaCostModal = (client) => {
    setSelectedClient(client);
    setBostaCost(client.bosta_cost || 0);
    setModalType('bostaCost');
  };

  // فتح الـ Client Dashboard في تاب جديد
  const openClientDashboard = (clientAuthUid) => {
    // نتأكد إن user موجود
    if (!user?.uid) {
      alert('خطأ: لم يتم العثور على بيانات Super Admin. حاول تسجيل الدخول مرة أخرى.');
      console.error('user.uid is undefined:', user);
      return;
    }
    
    console.log('🔍 Debug openClientDashboard:');
    console.log('  - clientAuthUid:', clientAuthUid);
    console.log('  - Admin UID:', user.uid);
    
    // 🔥 الداشبورد الموحد لكل العملاء (Shopify + EasyOrder)
    const dashboardDomain = 'https://dashboard-daf-api.dafaiagency.shop';
    
    // نبعت UID العميل + UID الـ Super Admin
    const url = `${dashboardDomain}/dashboard?uid=${encodeURIComponent(clientAuthUid)}&admin=${encodeURIComponent(user.uid)}`;
    console.log('🔗 Opening unified dashboard:', url);
    window.open(url, '_blank');
  };

  // 🔥 حذف العميل نهائياً
  const deleteClient = async (client) => {
    const confirmDelete = window.confirm(
      `⚠️ تحذير: هل أنت متأكد من حذف العميل "${client.brand_name}"؟\n\n` +
      `سيتم حذف:\n` +
      `- حساب العميل من clients_api\n` +
      `- حساب المستخدم من Authentication\n` +
      `- جميع الطلبات المرتبطة به\n\n` +
      `هذا الإجراء لا يمكن التراجع عنه!`
    );

    if (!confirmDelete) return;

    // تأكيد إضافي
    const confirmAgain = window.prompt(
      `للتأكيد، اكتب اسم البراند "${client.brand_name}" بالضبط:`
    );

    if (confirmAgain !== client.brand_name) {
      alert('الاسم غير مطابق. تم إلغاء الحذف.');
      return;
    }

    setIsProcessing(true);
    try {
      console.log('🗑️ Deleting client:', client.brand_name);
      console.log('  - Client ID (doc):', client.id);
      console.log('  - Auth UID:', client.auth_uid);
      
      // استدعاء Cloud Function لحذف المستخدم من Auth والبيانات
      await callDeleteClient(client);
      
      alert(`✅ تم حذف العميل "${client.brand_name}" بنجاح`);
    } catch (error) {
      console.error('Error deleting client:', error);
      alert(`❌ فشل حذف العميل: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // إضافة/تقليل الرصيد
  const updateBalance = async (operation) => {
    if (!balanceAmount || isNaN(balanceAmount) || Number(balanceAmount) <= 0) {
      alert("من فضلك أدخل مبلغ صحيح");
      return;
    }

    if (isProcessing) return;
    
    const amount = Number(balanceAmount);
    const confirmMsg = operation === 'add' 
      ? `هل تريد إضافة $${amount} لرصيد ${selectedClient.brand_name}؟`
      : `هل تريد خصم $${amount} من رصيد ${selectedClient.brand_name}؟`;
    
    if (!window.confirm(confirmMsg)) return;

    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', selectedClient.id);
      await updateDoc(clientRef, {
        balance: increment(operation === 'add' ? amount : -amount)
      });
      
      // 🔥 تسجيل العملية في revenue_transactions
      const transactionData = {
        brand_name: selectedClient.brand_name,
        client_id: selectedClient.client_id,
        platform: selectedClient.platform || 'غير محدد',
        operation_type: operation === 'add' ? 'إضافة' : 'خصم',
        amount: amount,
        balance_before: selectedClient.balance || 0,
        balance_after: (selectedClient.balance || 0) + (operation === 'add' ? amount : -amount),
        admin_uid: user?.uid || 'unknown',
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleString('ar-EG', { timeZone: 'Africa/Cairo' })
      };
      
      await addDoc(collection(db, 'revenue_transactions'), transactionData);
      
      // 🔥 تحديث إجمالي الإيرادات (فقط عند الإضافة)
      if (operation === 'add') {
        const revenueRef = doc(db, 'financial_tracking', 'total_revenue');
        await updateDoc(revenueRef, {
          amount: increment(amount),
          lastUpdated: new Date().toISOString()
        }).catch(async (error) => {
          // لو الـ document مش موجود، أنشئه
          if (error.code === 'not-found') {
            await setDoc(revenueRef, {
              amount: amount,
              createdAt: new Date().toISOString(),
              lastUpdated: new Date().toISOString()
            });
          }
        });
      }
      
      setSelectedClient(null);
      setBalanceAmount('');
      setModalType('');
      alert("تم تحديث الرصيد بنجاح!");
    } catch (error) {
      console.error("Error updating balance:", error);
      alert("حدث خطأ أثناء تحديث الرصيد");
    } finally {
      setIsProcessing(false);
    }
  };

  // تحديث سعر الرسالة
  const updateOrderCost = async () => {
    if (!orderCost || isNaN(orderCost) || Number(orderCost) <= 0) {
      alert("من فضلك أدخل سعر صحيح");
      return;
    }

    if (isProcessing) return;
    
    const newCost = Number(orderCost);
    const confirmMsg = `هل تريد تغيير سعر الرسالة لـ ${selectedClient.brand_name} إلى $${newCost}؟`;
    
    if (!window.confirm(confirmMsg)) return;

    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', selectedClient.id);
      await updateDoc(clientRef, {
        order_cost: newCost
      });
      
      setSelectedClient(null);
      setOrderCost('');
      setModalType('');
      alert("تم تحديث سعر الرسالة بنجاح!");
    } catch (error) {
      console.error("Error updating order cost:", error);
      alert("حدث خطأ أثناء تحديث سعر الرسالة");
    } finally {
      setIsProcessing(false);
    }
  };

  // 🔥 تحديث سعر Feedback
  const updateFeedbackCost = async () => {
    if (!feedbackCost || isNaN(feedbackCost) || Number(feedbackCost) < 0) {
      alert("من فضلك أدخل سعر صحيح");
      return;
    }

    if (isProcessing) return;
    
    const newCost = Number(feedbackCost);
    const confirmMsg = `هل تريد تغيير سعر Feedback لـ ${selectedClient.brand_name} إلى $${newCost}؟`;
    
    if (!window.confirm(confirmMsg)) return;

    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', selectedClient.id);
      await updateDoc(clientRef, {
        feedback_cost: newCost
      });
      
      setSelectedClient(null);
      setFeedbackCost('');
      setModalType('');
      alert("تم تحديث سعر Feedback بنجاح!");
    } catch (error) {
      console.error("Error updating feedback cost:", error);
      alert("حدث خطأ أثناء تحديث سعر Feedback");
    } finally {
      setIsProcessing(false);
    }
  };

  // 🔥 تحديث سعر Bosta
  const updateBostaCost = async () => {
    if (!bostaCost && bostaCost !== 0 || isNaN(bostaCost) || Number(bostaCost) < 0) {
      alert("من فضلك أدخل سعر صحيح");
      return;
    }

    if (isProcessing) return;
    
    const newCost = Number(bostaCost);
    const confirmMsg = `هل تريد تغيير سعر Bosta لـ ${selectedClient.brand_name} إلى $${newCost}؟`;
    
    if (!window.confirm(confirmMsg)) return;

    setIsProcessing(true);
    try {
      const clientRef = doc(db, 'clients_api', selectedClient.id);
      await updateDoc(clientRef, {
        bosta_cost: newCost
      });
      
      setSelectedClient(null);
      setBostaCost('');
      setModalType('');
      alert("تم تحديث سعر Bosta بنجاح!");
    } catch (error) {
      console.error("Error updating bosta cost:", error);
      alert("حدث خطأ أثناء تحديث سعر Bosta");
    } finally {
      setIsProcessing(false);
    }
  };

  // 🔥 تصدير جدول العملاء إلى Excel
  const exportClientsToExcel = () => {
    try {
      // تحضير البيانات للتصدير
      const exportData = filteredClients.map(client => {
        const orderStats = getClientOrderStats(client.brand_name);
        return {
          'اسم البراند': client.brand_name || 'N/A',
          'المنصة': client.platform === 'shopify' ? 'Shopify' : 
                    client.platform === 'easyorder' ? 'EasyOrder' : 'غير محدد',
          'الإيميل': client.email || 'N/A',
          'الهاتف': client.brand_owner_whatsapp || 'N/A',
          'الرصيد ($)': client.balance || 0,
          'سعر الرسالة ($)': client.order_cost || 0.06,
          'إجمالي الأوردرات': orderStats.total,
          'أوردرات مؤكدة': orderStats.confirmed,
          'أوردرات ملغاة': orderStats.canceled,
          'أوردرات AI': orderStats.aiCreated,
          'أوردرات غير واضحة': orderStats.unclear,
          'حالة الخدمة': client.isActive ? 'مُفعل' : 'مُعطل',
          'حالة البوت': client.chatbot ? 'نشط' : 'مُوقف',
          'السيرفر': client.server_name || 'N/A',
          'تاريخ التسجيل': client.createdAt ? new Date(client.createdAt.toDate()).toLocaleDateString('ar-EG') : 'N/A'
        };
      });

      // إنشاء ورقة عمل
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // تعديل عرض الأعمدة
      const colWidths = [
        { wch: 20 }, // اسم البراند
        { wch: 12 }, // المنصة
        { wch: 25 }, // الإيميل
        { wch: 15 }, // الهاتف
        { wch: 12 }, // الرصيد
        { wch: 15 }, // سعر الرسالة
        { wch: 15 }, // إجمالي الأوردرات
        { wch: 15 }, // مؤكدة
        { wch: 15 }, // ملغاة
        { wch: 12 }, // AI
        { wch: 15 }, // غير واضحة
        { wch: 12 }, // حالة الخدمة
        { wch: 12 }, // حالة البوت
        { wch: 18 }, // السيرفر
        { wch: 15 }  // تاريخ التسجيل
      ];
      ws['!cols'] = colWidths;

      // إنشاء مصنف
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'العملاء');

      // تحميل الملف
      const fileName = `عملاء_${new Date().toLocaleDateString('ar-EG').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ تم تصدير البيانات بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تصدير البيانات:', error);
      alert('حدث خطأ أثناء تصدير البيانات');
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          🔐 Super Admin Dashboard
        </h1>
        <button onClick={handleLogout} style={styles.logoutButton}>
          تسجيل خروج
        </button>
      </div>

      {/* الإحصائيات */}
      <div style={styles.statsContainer}>
        <div style={{...styles.statCard, borderColor: '#bd93f9'}}>
          <h4 style={styles.statLabel}>إجمالي العملاء</h4>
          <p style={{...styles.statValue, color: '#bd93f9'}}>{stats.totalClients}</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#50fa7b'}}>
          <h4 style={styles.statLabel}>خدمة مُفعلة</h4>
          <p style={{...styles.statValue, color: '#50fa7b'}}>{stats.activeClients}</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#ff5555'}}>
          <h4 style={styles.statLabel}>خدمة مُعطلة</h4>
          <p style={{...styles.statValue, color: '#ff5555'}}>{stats.inactiveClients}</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#8be9fd'}}>
          <h4 style={styles.statLabel}>بوتات نشطة</h4>
          <p style={{...styles.statValue, color: '#8be9fd'}}>{stats.botsActive}</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#f1fa8c'}}>
          <h4 style={styles.statLabel}>إجمالي الأرصدة</h4>
          <p style={{...styles.statValue, color: '#f1fa8c'}}>${stats.totalBalance.toFixed(2)}</p>
          <p style={{fontSize: '12px', color: '#6272a4', margin: '5px 0 0 0'}}>الرصيد الحالي</p>
        </div>
        <div style={{...styles.statCard, borderColor: '#ff79c6', background: 'linear-gradient(135deg, #282a36 0%, #44475a 100%)'}}>
          <h4 style={styles.statLabel}>💎 إجمالي الإيرادات</h4>
          <p style={{...styles.statValue, color: '#ff79c6'}}>${totalRevenue.toFixed(2)}</p>
          <p style={{fontSize: '12px', color: '#bd93f9', margin: '5px 0 0 0'}}>كل الفلوس المضافة</p>
        </div>
      </div>

      {/* إحصائيات الأوردرات */}
      <div style={styles.statsContainer}>
        <div style={{...styles.statCard, borderColor: '#50fa7b'}}>
          <h4 style={styles.statLabel}>أوردرات مؤكدة ✅</h4>
          <p style={{...styles.statValue, color: '#50fa7b'}}>{stats.totalConfirmed}</p>
          <p style={styles.statPercentage}>
            {stats.totalOrders > 0 ? ((stats.totalConfirmed / stats.totalOrders) * 100).toFixed(1) : 0}%
          </p>
        </div>
        <div style={{...styles.statCard, borderColor: '#ff5555'}}>
          <h4 style={styles.statLabel}>أوردرات ملغاة ❌</h4>
          <p style={{...styles.statValue, color: '#ff5555'}}>{stats.totalCanceled}</p>
          <p style={styles.statPercentage}>
            {stats.totalOrders > 0 ? ((stats.totalCanceled / stats.totalOrders) * 100).toFixed(1) : 0}%
          </p>
        </div>
        <div style={{...styles.statCard, borderColor: '#8be9fd'}}>
          <h4 style={styles.statLabel}>أوردرات AI 🤖</h4>
          <p style={{...styles.statValue, color: '#8be9fd'}}>{stats.totalAiCreated}</p>
          <p style={styles.statPercentage}>
            {stats.totalOrders > 0 ? ((stats.totalAiCreated / stats.totalOrders) * 100).toFixed(1) : 0}%
          </p>
        </div>
        <div style={{...styles.statCard, borderColor: '#f1fa8c'}}>
          <h4 style={styles.statLabel}>غير واضحة ❓</h4>
          <p style={{...styles.statValue, color: '#f1fa8c'}}>{stats.totalUnclear}</p>
          <p style={styles.statPercentage}>
            {stats.totalOrders > 0 ? ((stats.totalUnclear / stats.totalOrders) * 100).toFixed(1) : 0}%
          </p>
        </div>
        <div style={{...styles.statCard, borderColor: '#bd93f9'}}>
          <h4 style={styles.statLabel}>إجمالي الأوردرات 📦</h4>
          <p style={{...styles.statValue, color: '#bd93f9'}}>{stats.totalOrders}</p>
          <p style={styles.statPercentage}>100%</p>
        </div>
      </div>

      {/* شريط البحث */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="🔍 ابحث بالاسم، الإيميل، أو الهاتف..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')} 
            style={styles.clearButton}
          >
            مسح ✕
          </button>
        )}
      </div>

      {/* 🔥 أزرار فلتر المنصات */}
      <div style={styles.platformFilterContainer}>
        <button 
          onClick={() => setPlatformFilter('all')}
          style={{
            ...styles.platformButton, 
            ...(platformFilter === 'all' && styles.activePlatformButton)
          }}
        >
          الكل ({stats.totalClients})
        </button>
        <button 
          onClick={() => setPlatformFilter('shopify')}
          style={{
            ...styles.platformButton, 
            ...(platformFilter === 'shopify' && styles.activePlatformButton)
          }}
        >
          🛒 Shopify ({stats.shopifyClients})
        </button>
        <button 
          onClick={() => setPlatformFilter('easyorder')}
          style={{
            ...styles.platformButton, 
            ...(platformFilter === 'easyorder' && styles.activePlatformButton)
          }}
        >
          📦 EasyOrder ({stats.easyorderClients})
        </button>
      </div>

      {/* جدول العملاء */}
      <div style={styles.tableCard}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h2 style={styles.tableTitle}>
            جميع العملاء 
            <span style={styles.countBadge}>{filteredClients.length}</span>
          </h2>
          <button 
            onClick={exportClientsToExcel}
            style={styles.exportButton}
            title="تصدير إلى Excel"
          >
            📥 تصدير Excel
          </button>
        </div>
        
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>اسم البراند</th>
                <th style={styles.th}>المنصة</th>
                <th style={styles.th}>الإيميل</th>
                <th style={styles.th}>الهاتف</th>
                <th style={styles.th}>الرصيد ($)</th>
                <th style={styles.th}>سعر الرسالة ($)</th>
                <th style={styles.th}>الأوردرات</th>
                <th style={styles.th}>حالة الخدمة</th>
                <th style={styles.th}>حالة البوت</th>
                <th style={styles.th}>Dashboard</th>
                <th style={styles.th}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={{...styles.td, textAlign: 'center', color: '#6272a4'}}>
                    جاري تحميل العملاء...
                  </td>
                </tr>
              ) : filteredClients.length > 0 ? (
                filteredClients.map((client, index) => {
                  const orderStats = getClientOrderStats(client.brand_name);
                  const isExpanded = expandedClient === client.id;
                  
                  return (
                    <React.Fragment key={client.id}>
                      <tr style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                        <td style={styles.td}>{client.brand_name || 'N/A'}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.platformBadge,
                            backgroundColor: client.platform === 'shopify' ? '#96bf48' : 
                                            client.platform === 'easyorder' ? '#ff6b6b' : '#6272a4'
                          }}>
                            {client.platform === 'shopify' ? '🛒 Shopify' : 
                             client.platform === 'easyorder' ? '📦 EasyOrder' : '❓ غير محدد'}
                          </span>
                        </td>
                        <td style={styles.td}>{client.email || 'N/A'}</td>
                        <td style={styles.td}>{client.brand_owner_whatsapp || 'N/A'}</td>
                        <td style={{...styles.td, fontWeight: 'bold', color: '#f1fa8c'}}>
                          ${(client.balance || 0).toFixed(2)}
                        </td>
                        <td style={{...styles.td, fontWeight: 'bold', color: '#8be9fd'}}>
                          ${(client.order_cost || 0.06).toFixed(2)}
                        </td>
                        <td style={styles.td}>
                          <div 
                            style={styles.ordersCell}
                            onClick={() => setExpandedClient(isExpanded ? null : client.id)}
                          >
                            <span style={styles.expandIcon}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{fontWeight: 'bold', color: '#bd93f9'}}>
                              {orderStats.total}
                            </span>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            backgroundColor: client.isActive ? '#50fa7b' : '#ff5555'
                          }}>
                            {client.isActive ? 'مُفعل ✓' : 'مُعطل ✕'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            backgroundColor: client.chatbot ? '#8be9fd' : '#6272a4'
                          }}>
                            {client.chatbot ? 'نشط 🤖' : 'مُوقف'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <button 
                            onClick={() => openClientDashboard(client.auth_uid)}
                            style={styles.dashboardButton}
                            title="فتح Dashboard العميل"
                          >
                            🔗 Dashboard
                          </button>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionsContainer}>
                            <button 
                              onClick={() => openBalanceModal(client)}
                              style={styles.balanceButton}
                              disabled={isProcessing}
                            >
                              💰 الرصيد
                            </button>
                            <button 
                              onClick={() => openOrderCostModal(client)}
                              style={{...styles.balanceButton, backgroundColor: '#8be9fd'}}
                              disabled={isProcessing}
                            >
                              💵 سعر الرسالة
                            </button>
                            {/* 🔥 زرار تعديل سعر Feedback - لكل المنصات */}
                            <button 
                              onClick={() => openFeedbackCostModal(client)}
                              style={{...styles.balanceButton, backgroundColor: '#bd93f9'}}
                              disabled={isProcessing}
                              title="تعديل سعر رسائل Feedback"
                            >
                              ⭐ سعر Feedback
                            </button>
                            {/* 🔥 زرار تعديل سعر Bosta */}
                            <button 
                              onClick={() => openBostaCostModal(client)}
                              style={{...styles.balanceButton, backgroundColor: '#ff5555'}}
                              disabled={isProcessing}
                              title="تعديل سعر رسائل Bosta"
                            >
                              📦 سعر Bosta
                            </button>
                            <button 
                              onClick={() => toggleServiceStatus(client.id, client.isActive)}
                              style={{
                                ...styles.actionButton,
                                backgroundColor: client.isActive ? '#ff5555' : '#50fa7b'
                              }}
                              disabled={isProcessing}
                            >
                              {client.isActive ? '⏸️ إيقاف' : '▶️ تفعيل'}
                            </button>
                            <button 
                              onClick={() => toggleBotStatus(client.id, client.chatbot)}
                              style={{
                                ...styles.actionButton,
                                backgroundColor: client.chatbot ? '#6272a4' : '#8be9fd',
                                color: client.chatbot ? '#f8f8f2' : '#282a36'
                              }}
                              disabled={isProcessing}
                            >
                              {client.chatbot ? '🛑 إيقاف بوت' : '🤖 تفعيل بوت'}
                            </button>
                            <button 
                              onClick={() => deleteClient(client)}
                              style={{
                                ...styles.actionButton,
                                backgroundColor: '#ff5555',
                                color: '#f8f8f2'
                              }}
                              disabled={isProcessing}
                              title="حذف العميل نهائياً"
                            >
                              🗑️ حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* صف التفاصيل الموسع */}
                      {isExpanded && (
                        <tr style={{backgroundColor: 'rgba(139, 233, 253, 0.05)'}}>
                          <td colSpan="11" style={{...styles.td, padding: '15px 20px'}}>
                            <div style={styles.expandedStats}>
                              <div style={styles.expandedStatItem}>
                                <span style={{color: '#50fa7b', fontSize: '18px', fontWeight: 'bold'}}>
                                  {orderStats.confirmed}
                                </span>
                                <span style={{color: '#e0e0e0', fontSize: '14px'}}>مؤكدة ✅</span>
                              </div>
                              <div style={styles.expandedStatItem}>
                                <span style={{color: '#ff5555', fontSize: '18px', fontWeight: 'bold'}}>
                                  {orderStats.canceled}
                                </span>
                                <span style={{color: '#e0e0e0', fontSize: '14px'}}>ملغاة ❌</span>
                              </div>
                              <div style={styles.expandedStatItem}>
                                <span style={{color: '#8be9fd', fontSize: '18px', fontWeight: 'bold'}}>
                                  {orderStats.aiCreated}
                                </span>
                                <span style={{color: '#e0e0e0', fontSize: '14px'}}>AI 🤖</span>
                              </div>
                              <div style={styles.expandedStatItem}>
                                <span style={{color: '#f1fa8c', fontSize: '18px', fontWeight: 'bold'}}>
                                  {orderStats.unclear}
                                </span>
                                <span style={{color: '#e0e0e0', fontSize: '14px'}}>غير واضحة ❓</span>
                              </div>
                              <div style={styles.expandedStatItem}>
                                <span style={{color: '#bd93f9', fontSize: '18px', fontWeight: 'bold'}}>
                                  {orderStats.total}
                                </span>
                                <span style={{color: '#e0e0e0', fontSize: '14px'}}>المجموع 📦</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" style={{...styles.td, textAlign: 'center', color: '#6272a4'}}>
                    {searchTerm || platformFilter !== 'all' ? 'لا توجد نتائج للبحث' : 'لا يوجد عملاء مسجلين'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* إحصائيات السيرفرات المباشرة */}
      <ServerStats />

      {/* سجل عمليات الرصيد */}
      <TransactionHistory />

      {/* إدارة السيرفرات */}
      <ServerManagement />

      {/* مودال تعديل الرصيد */}
      {selectedClient && modalType === 'balance' && (
        <div style={styles.modalBackdrop} onClick={() => !isProcessing && setSelectedClient(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              تعديل رصيد: {selectedClient.brand_name}
            </h3>
            <p style={styles.currentBalance}>
              الرصيد الحالي: <strong>${(selectedClient.balance || 0).toFixed(2)}</strong>
            </p>
            
            <input
              type="number"
              placeholder="المبلغ"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
              style={styles.balanceInput}
              disabled={isProcessing}
              min="1"
            />
            
            <div style={styles.modalActions}>
              <button 
                onClick={() => updateBalance('add')}
                style={{...styles.modalButton, backgroundColor: '#50fa7b'}}
                disabled={isProcessing || !balanceAmount}
              >
                ➕ إضافة
              </button>
              <button 
                onClick={() => updateBalance('subtract')}
                style={{...styles.modalButton, backgroundColor: '#ff5555'}}
                disabled={isProcessing || !balanceAmount}
              >
                ➖ خصم
              </button>
              <button 
                onClick={() => !isProcessing && setSelectedClient(null)}
                style={{...styles.modalButton, backgroundColor: '#6272a4'}}
                disabled={isProcessing}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال تعديل سعر الرسالة */}
      {selectedClient && modalType === 'orderCost' && (
        <div style={styles.modalBackdrop} onClick={() => !isProcessing && setSelectedClient(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              تعديل سعر الرسالة: {selectedClient.brand_name}
            </h3>
            <p style={styles.currentBalance}>
              السعر الحالي: <strong>{selectedClient.order_cost || 0.06}</strong> $
            </p>
            
            <input
              type="number"
              placeholder="السعر الجديد"
              value={orderCost}
              onChange={(e) => setOrderCost(e.target.value)}
              style={styles.balanceInput}
              disabled={isProcessing}
              min="0.01"
              step="0.01"
            />
            
            <div style={styles.modalActions}>
              <button 
                onClick={updateOrderCost}
                style={{...styles.modalButton, backgroundColor: '#8be9fd', color: '#282a36'}}
                disabled={isProcessing || !orderCost}
              >
                ✅ تحديث
              </button>
              <button 
                onClick={() => !isProcessing && setSelectedClient(null)}
                style={{...styles.modalButton, backgroundColor: '#6272a4'}}
                disabled={isProcessing}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 مودال تعديل سعر Feedback */}
      {selectedClient && modalType === 'feedbackCost' && (
        <div style={styles.modalBackdrop} onClick={() => !isProcessing && setSelectedClient(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              ⭐ تعديل سعر Feedback: {selectedClient.brand_name}
            </h3>
            <p style={styles.currentBalance}>
              السعر الحالي: <strong>${(selectedClient.feedback_cost || 0).toFixed(2)}</strong>
            </p>
            <p style={{color: '#6272a4', fontSize: '13px', margin: '10px 0', textAlign: 'center'}}>
              (سعر رسالة طلب التقييم من العملاء بعد استلام المنتج)
            </p>
            
            <input
              type="number"
              placeholder="السعر الجديد"
              value={feedbackCost}
              onChange={(e) => setFeedbackCost(e.target.value)}
              style={styles.balanceInput}
              disabled={isProcessing}
              min="0"
              step="0.01"
            />
            
            <div style={styles.modalActions}>
              <button 
                onClick={updateFeedbackCost}
                style={{...styles.modalButton, backgroundColor: '#bd93f9', color: '#282a36'}}
                disabled={isProcessing || feedbackCost === ''}
              >
                ✅ تحديث
              </button>
              <button 
                onClick={() => !isProcessing && setSelectedClient(null)}
                style={{...styles.modalButton, backgroundColor: '#6272a4'}}
                disabled={isProcessing}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 مودال تعديل سعر Bosta */}
      {selectedClient && modalType === 'bostaCost' && (
        <div style={styles.modalBackdrop} onClick={() => !isProcessing && setSelectedClient(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              📦 تعديل سعر Bosta: {selectedClient.brand_name}
            </h3>
            <p style={styles.currentBalance}>
              السعر الحالي: <strong>${(selectedClient.bosta_cost || 0).toFixed(2)}</strong>
            </p>
            <p style={{color: '#6272a4', fontSize: '13px', margin: '10px 0', textAlign: 'center'}}>
              (سعر رسالة Bosta للشحن والتتبع)
            </p>
            
            <input
              type="number"
              placeholder="السعر الجديد"
              value={bostaCost}
              onChange={(e) => setBostaCost(e.target.value)}
              style={styles.balanceInput}
              disabled={isProcessing}
              min="0"
              step="0.01"
            />
            
            <div style={styles.modalActions}>
              <button 
                onClick={updateBostaCost}
                style={{...styles.modalButton, backgroundColor: '#ff5555', color: '#f8f8f2'}}
                disabled={isProcessing || bostaCost === ''}
              >
                ✅ تحديث
              </button>
              <button 
                onClick={() => !isProcessing && setSelectedClient(null)}
                style={{...styles.modalButton, backgroundColor: '#6272a4'}}
                disabled={isProcessing}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// الستايلات
const styles = {
  container: {
    padding: '25px',
    backgroundColor: '#1e1e2f',
    color: '#e0e0e0',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  title: {
    color: '#bd93f9',
    margin: 0,
    fontSize: '2em',
  },
  logoutButton: {
    background: '#ff5555',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
  },
  statsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    flex: '1 1 200px',
    background: '#282a36',
    padding: '20px',
    borderRadius: '10px',
    border: '2px solid',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
  },
  statLabel: {
    margin: '0 0 10px 0',
    fontSize: '1em',
    color: '#f8f8f2',
  },
  statValue: {
    margin: 0,
    fontSize: '2.5em',
    fontWeight: 'bold',
  },
  statPercentage: {
    fontSize: '1.2em',
    fontWeight: '600',
    margin: '5px 0 0 0',
    color: '#8be9fd',
    opacity: 0.9,
  },
  searchContainer: {
    marginBottom: '25px',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    padding: '12px 15px',
    fontSize: '16px',
    background: '#282a36',
    border: '1px solid #6272a4',
    borderRadius: '5px',
    color: '#f8f8f2',
    outline: 'none',
  },
  clearButton: {
    padding: '12px 20px',
    background: '#ff5555',
    color: '#f8f8f2',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  // 🔥 ستايلات فلتر المنصات
  platformFilterContainer: {
    marginBottom: '25px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  platformButton: {
    padding: '12px 24px',
    background: '#282a36',
    border: '2px solid #6272a4',
    color: '#f8f8f2',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '15px',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  activePlatformButton: {
    background: '#bd93f9',
    borderColor: '#bd93f9',
    color: '#282a36',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(189, 147, 249, 0.4)',
  },
  platformBadge: {
    padding: '5px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#282a36',
    display: 'inline-block',
  },
  tableCard: {
    background: '#282a36',
    padding: '25px',
    borderRadius: '10px',
    border: '1px solid #44475a',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
  },
  tableTitle: {
    color: '#f8f8f2',
    marginTop: 0,
    marginBottom: '20px',
  },
  countBadge: {
    backgroundColor: '#bd93f9',
    color: '#282a36',
    padding: '5px 12px',
    borderRadius: '12px',
    fontSize: '0.8em',
    fontWeight: 'bold',
    marginRight: '15px',
  },
  tableWrapper: {
    maxHeight: '600px',
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    borderBottom: '2px solid #6272a4',
    padding: '12px 15px',
    background: '#44475a',
    textAlign: 'right',
    fontSize: '14px',
    color: '#f8f8f2',
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    borderBottom: '1px solid #44475a',
    padding: '12px 15px',
    textAlign: 'right',
    fontSize: '14px',
    color: '#e0e0e0',
  },
  trEven: {
    backgroundColor: 'transparent',
  },
  trOdd: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
  statusBadge: {
    padding: '5px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#282a36',
    display: 'inline-block',
  },
  actionsContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  balanceButton: {
    background: '#f1fa8c',
    color: '#282a36',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    transition: 'all 0.3s ease',
  },
  actionButton: {
    border: 'none',
    padding: '8px 12px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    transition: 'all 0.3s ease',
    color: '#282a36',
  },
  dashboardButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(5px)',
  },
  modalContent: {
    background: '#282a36',
    padding: '30px',
    borderRadius: '10px',
    border: '2px solid #bd93f9',
    boxShadow: '0 8px 25px rgba(0,0,0,0.7)',
    color: '#f8f8f2',
    maxWidth: '400px',
    width: '90%',
  },
  modalTitle: {
    color: '#bd93f9',
    marginTop: 0,
    marginBottom: '20px',
  },
  currentBalance: {
    fontSize: '16px',
    marginBottom: '20px',
    color: '#f1fa8c',
  },
  balanceInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    background: '#1e1e2f',
    border: '1px solid #6272a4',
    borderRadius: '5px',
    color: '#f8f8f2',
    marginBottom: '20px',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between',
  },
  modalButton: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    color: '#282a36',
    transition: 'all 0.3s ease',
  },
  ordersCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  expandIcon: {
    fontSize: '12px',
    color: '#8be9fd',
    transition: 'transform 0.2s ease',
  },
  expandedStats: {
    display: 'flex',
    gap: '30px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  expandedStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    padding: '10px 20px',
    background: 'rgba(40, 42, 54, 0.5)',
    borderRadius: '8px',
    border: '1px solid #44475a',
  },
  exportButton: {
    background: 'linear-gradient(135deg, #50fa7b 0%, #8be9fd 100%)',
    color: '#282a36',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(80, 250, 123, 0.3)',
  },
};

export default SuperAdminDashboard;
